/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kSPRestCoreExecute - kSPRestCore's request execution (RestExecute, RestExecuteWebService, RestExecuteExternal) and request building.
 * Split out of kSPRestCore.ts (2026-10-19, owner: smaller files for the reuse reviewer). The bodies are
 * moved VERBATIM except that `this` is the explicit first parameter `Self` (private/protected members are
 * read as Self["Name"]); kSPRestCore keeps every member as a same-signature delegating member.
 */

import { getCoreUtils, kNotNullOrUndefined, nz, nb, doCallBack } from "./kCoreHandler";
import { RestRequestOptions, RestResponseError } from "./kRestExecutor";
import { RestRequestContext } from "./kSPRestRequestCore";
import type { kSPRestCore } from "./kSPRestCore";

export class kSPRestCoreExecute {
    // ── Core REST execution ──────────────────────────────────────────────────

    // DOCUMENTED EXCEPTION: moved body of kSPRestCore.RestExecute - its gate, try/catch and handler label "kSPRestCore.RestExecute" stay verbatim, so error reports are unchanged.
    public static RestExecute = (Self: typeof kSPRestCore, CallType: string, RestCall: string, Callback: any, Data?: string | Record<string, any> | Blob | ArrayBuffer | null, Comments?: string, IgnoreError?: boolean, CustomHeader?: string | Record<string, string> | null, Binary?: boolean, ErrorHandler?: any, IsText?: boolean, TolerateStatuses?: number[]): void => {
        // Settles rather than returning bare, like RestExecuteExternal (r3-04, owner decision 2026-10-10).
        if (getCoreUtils().KErrorMode) {
            Self["_SettleUnsent"](ErrorHandler, "error mode engaged before the request was sent");
            return;
        }
        try {
            if (!Self._EnsureExecutorReady("kSPRestCore.RestExecute")) {
                Self["_SettleUnsent"](ErrorHandler, "kSPRestCore.Executor is not set");
                return;
            }
            const Options: RestRequestOptions =
                Self["_BuildRequestOptions"](CallType, RestCall, Data, CustomHeader, Binary, IsText);
            const Context: RestRequestContext =
                Self["_BuildRequestContext"](Options, Callback, Comments, IgnoreError, ErrorHandler, TolerateStatuses);
            Self.Executor.ExecuteRequest(
                Options, Context.OnSuccess, Context.OnError
            );
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore.RestExecute");
        }
    };

    /**
     * Call a NON-SharePoint HTTPS endpoint - a Logic App, a webhook, a licensing service - with
     * the same callback contract, retry policy and error routing as every other call in here.
     *
     * WHY IT IS NOT RestExecuteExternal. That method is for another SharePoint WEB: it composes
     * `TargetWeb + RestCall`, and for anything other than a GET it first fetches a FORM DIGEST
     * from the target web. Point it at a Logic App and the digest request goes to a host that has
     * no `/_api/contextinfo`, so the real call is never made. It also leaves `IsTextResponse`
     * false, so a service answering a bare word ("Registered") fails `JSON.parse`, which the
     * executor reports through the global handler - i.e. a perfectly good response flips
     * `KErrorMode` and silently gates the whole application.
     *
     * WHY IT EXISTS AT ALL. Without it the only way to call such an endpoint from this estate was
     * a raw promise-based `fetch()`, which is banned by the house rule (callbacks, never promises)
     * and which also opts out of the retry/backoff and the single error channel. kSPReg2's
     * product-registration call was written that way; this is the shared piece it should have had.
     *
     * @param Method       "POST", "GET", ...
     * @param Url          the ABSOLUTE url, used exactly as given.
     * @param Payload      object (serialised as JSON) or string; null for none.
     * @param Callback     receives the response TEXT when IsText is true (the default), otherwise
     *                     the parsed JSON.
     * @param ErrorHandler optional; receives the RestResponseError after retries are exhausted.
     * @param IgnoreError  true keeps a failure away from the global handler - only when the
     *                     caller has a genuinely correct fallback, and say so at the call site.
     * @param IsText       defaults TRUE: a plain web service usually answers text, and guessing
     *                     JSON is the failure mode described above. Pass false for a JSON API.
     * @param ExtraHeaders optional; merged over the base headers. This is where a CREDENTIAL goes -
     *                     an API key, a bearer token, a single-use ticket. Never put one in the
     *                     query string: it lands in every access log and proxy cache along the way.
     * @param TolerateStatuses optional; statuses this caller considers the service's ANSWER rather
     *                     than a fault - settled to the ErrorHandler with no report, everything else
     *                     still reported in full. See RestRequestContext.TolerateStatuses. Added
     *                     2026-09-24, OPTIONAL so every existing caller is byte-unchanged. A web
     *                     service that authenticates its caller answers 401 on purpose, and reporting
     *                     that latched KErrorMode and killed the calling page (ContractsManager,
     *                     measured in its gate 11). IgnoreError was never the answer: it also hides
     *                     the 404 of a wrong address and every transport fault.
     * @param NoRetry optional opt-in: send once, even for a tolerated or retryable failure.
     *                     Use for single-use credentials; false retains existing automatic retries.
     */
    // DOCUMENTED EXCEPTION: moved body of kSPRestCore.RestExecuteWebService - its gate, try/catch and handler label "kSPRestCore.RestExecuteWebService" stay verbatim, so error reports are unchanged.
    public static RestExecuteWebService = (Self: typeof kSPRestCore, Method: string, Url: string, Payload?: string | Record<string, any> | null, Callback?: any, ErrorHandler?: any, IgnoreError?: boolean, Comments?: string, IsText?: boolean, ExtraHeaders?: Record<string, string> | null, TolerateStatuses?: number[], NoRetry: boolean = false): void => {
        // Settles rather than returning bare, like RestExecuteExternal (r3-04, owner decision 2026-10-10).
        if (getCoreUtils().KErrorMode) {
            Self["_SettleUnsent"](ErrorHandler, "error mode engaged before the request was sent");
            return;
        }
        try {
            if (!Self._EnsureExecutorReady("kSPRestCore.RestExecuteWebService")) {
                Self["_SettleUnsent"](ErrorHandler, "kSPRestCore.Executor is not set");
                return;
            }
            const Options: RestRequestOptions = new RestRequestOptions();
            Options.Method = Method.toUpperCase();
            Options.Url = Url;
            Options.IsExternal = true;
            Options.NoRetry = NoRetry;
            Options.IsBinaryResponse = false;
            Options.IsTextResponse = true;
            if (IsText === false) { Options.IsTextResponse = false; }
            // PLAIN JSON HEADERS, not BuildHeaders. BuildHeaders adds the SharePoint OData dialect
            // ("application/json;odata=verbose"), an "IF-MATCH: *" and an "X-HTTP-Method" verb
            // override - all meaningless to a web service, and the last one makes some gateways
            // answer 405 for a POST that was already a POST.
            Options.Headers = { "Content-Type": "application/json" };
            Self["_MergeExtraHeaders"](Options, ExtraHeaders);
            Self["_ApplyPayload"](Options, Payload);
            const Context: RestRequestContext =
                Self["_BuildRequestContext"](Options, Callback, Comments, IgnoreError, ErrorHandler,
                    TolerateStatuses);
            Self.Executor.ExecuteRequest(Options, Context.OnSuccess, Context.OnError);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore.RestExecuteWebService");
        }
    };

    /**
     * ⚠ BOTH REFUSALS SETTLE, they do not just return (LIVE-63, fixed 2026-09-10). A caller that
     * reached here with the gate up, or on a host whose executor was never initialised, used to get
     * NO callback at all — the LAW 1 stranding shape, in the transport itself. The request is not
     * sent either way; what changed is that a caller holding an ErrorHandler is TOLD, so it stops on
     * its own guard instead of waiting for an answer that can never come. `_SettleUnsent` carries the
     * reasoning and the caller-set audit that made it safe to add to shared code.
     *
     * `TimeoutMs` (last, OPTIONAL, 2026-09-24): a bound per attempt (RestRequestOptions.TimeoutMs);
     * omitted/0 = none, exactly as before. A timed-out attempt is retried like any status-0 failure;
     * the final one reaches ErrorCB un-reported, because the caller asked for the bound - see
     * RestRequestContext._IsOwnTimeout. Added for kSPLibraryFilesReader (SPArchive 1.0.2.0 stall).
     *
     * `IsText` (last, OPTIONAL, 2026-09-29, PX-100): true hands the Callback the response TEXT instead of
     * parsed JSON - a `$batch` answer is multipart text (kSPBatchReadCore with a TargetWeb). Omitted = JSON,
     * exactly as before.
     */
    // DOCUMENTED EXCEPTION: moved body of kSPRestCore.RestExecuteExternal - its gate, try/catch and handler label "kSPRestCore.RestExecuteExternal" stay verbatim, so error reports are unchanged.
    public static RestExecuteExternal = (Self: typeof kSPRestCore, RestCall: string, Callback?: any, Comments?: string, IgnoreError?: boolean, TargetWeb?: string, Binary?: boolean, ErrorCB?: any, Method?: string, CustomHeader?: string | Record<string, string> | null, Payload?: string | Record<string, any> | Blob | ArrayBuffer | null, TolerateStatuses?: number[], TimeoutMs?: number, IsText?: boolean): void => {
        // Settles rather than returning bare — see the docblock above (LIVE-63).
        if (getCoreUtils().KErrorMode) {
            Self["_SettleUnsent"](ErrorCB, "error mode engaged before the request was sent");
            return;
        }
        try {
            // _EnsureExecutorReady has ALREADY reported the missing executor, so the gate is up by
            // the time we get here: nothing left to report, only the caller left to release.
            if (!Self._EnsureExecutorReady("kSPRestCore.RestExecuteExternal")) {
                Self["_SettleUnsent"](ErrorCB, "kSPRestCore.Executor is not set");
                return;
            }
            let ResolvedMethod: string = "GET";
            if (kNotNullOrUndefined(Method)) { ResolvedMethod = nz(Method); }
            const Options: RestRequestOptions = new RestRequestOptions();
            Options.Method = ResolvedMethod;
            Options.IsBinaryResponse = nb(Binary);
            Options.IsExternal = true;
            Options.Url = nz(TargetWeb) + RestCall;
            if (typeof TimeoutMs === "number" && TimeoutMs > 0) { Options.TimeoutMs = TimeoutMs; }
            if (IsText === true) { Options.IsTextResponse = true; }
            Self["_ApplyHeaders"](Options, CustomHeader);
            Self["_ApplyPayload"](Options, Payload);
            const Context: RestRequestContext =
                Self["_BuildRequestContext"](Options, Callback, Comments, IgnoreError, ErrorCB,
                    TolerateStatuses);
            if (ResolvedMethod === "GET") {
                Self.Executor.ExecuteRequest(
                    Options, Context.OnSuccess, Context.OnError
                );
            } else {
                Self.Executor.FetchExternalDigest(
                    nz(TargetWeb), Context.OnExternalDigestReceived
                );
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore.RestExecuteExternal");
        }
    };

    /**
     * Release a caller whose request was never SENT — the gate was already up, or no executor was
     * ever registered. LIVE-63.
     *
     * ⚠ ONLY the ErrorHandler, never the success Callback. Several callers pass a success callback and
     * no error handler; handing THOSE a `null` "success" would be the fail-open direction — a caller
     * that has to treat "no rows" and "never asked" alike is exactly how a fabricated empty answer gets
     * built. A caller with no error handler is left as it always was, which is unchanged behaviour.
     *
     * ⚠ StatusCode 0, deliberately, and it is the truthful value: the request never reached a server.
     * `_IsToleratedStatus` already refuses to tolerate 0 for that reason, so a caller's
     * `TolerateStatuses` opt-in cannot swallow this by accident.
     *
     * WHY THIS IS SAFE TO ADD TO SHARED CODE. Both call sites are reached with `KErrorMode` ALREADY
     * true — the first tests it, and the second follows `_EnsureExecutorReady`, which reports (and so
     * latches) before answering false. So every house-style handler takes its own KErrorMode guard,
     * and anything it might call is guarded too. The live caller set was enumerated on 2026-09-10:
     * 88 call sites, all of them in `Admin/kReusableCoreTS` (33) or `MOSE/Function` (55) — no other
     * product calls this method at all, because `ReUsableTS/kSPRest2` has its own implementation.
     * Six of those sites pass no error handler and are untouched by construction.
     *
     * 2026-10-10 (r3-04, owner decision): `RestExecute` and `RestExecuteWebService` settle through here
     * too. Their callers (kReusableCoreTS, ReUsableTS incl. AIChats and kSPReg2, ContractsManager) were
     * audited first: every ErrorHandler either takes its own KErrorMode guard or is a deliberate
     * settle path. Note the handler runs SYNCHRONOUSLY, inside the caller's own RestExecute call.
     */
    // DOCUMENTED EXCEPTION: no KErrorMode guard, deliberately — this is reached BECAUSE the gate is up
    // or the transport is gone, so a guard would refuse the one job it exists for and reinstate the
    // stranding. The same words, for the same reason, as RestRequestContext._SettleError above.
    public static _SettleUnsent = (Self: typeof kSPRestCore, ErrorHandler: any, Reason: string): void => {
        try {
            if (!kNotNullOrUndefined(ErrorHandler)) { return; }
            const Failure: RestResponseError = new RestResponseError();
            Failure.StatusCode = 0;
            Failure.ResponseText = Reason;
            doCallBack(ErrorHandler, Failure);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore._SettleUnsent");
        }
    };

    // ── Private helpers ──────────────────────────────────────────────────────

    // DOCUMENTED EXCEPTION: moved body of kSPRestCore._BuildRequestOptions - its gate, try/catch and handler label "kSPRestCore._BuildRequestOptions" stay verbatim, so error reports are unchanged.
    public static _BuildRequestOptions = (Self: typeof kSPRestCore, CallType: string, RestCall: string, Data: string | Record<string, any> | Blob | ArrayBuffer | null | undefined, CustomHeader: string | Record<string, string> | null | undefined, Binary: boolean | undefined, IsText: boolean | undefined): RestRequestOptions => {
        if (getCoreUtils().KErrorMode) { return new RestRequestOptions(); }
        try {
            const Options: RestRequestOptions = new RestRequestOptions();
            Options.Method = CallType.toUpperCase();
            Options.IsBinaryResponse = nb(Binary);
            Options.IsTextResponse = nb(IsText);
            Options.Url = Self.URLs.CurrSiteFullURL + RestCall;
            Self["_ApplyHeaders"](Options, CustomHeader);
            Self["_ApplyPayload"](Options, Data);
            return Options;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore._BuildRequestOptions");
            return new RestRequestOptions();
        }
    };

    /** MERGE a caller's header object over the standard headers, never REPLACE them.
     *
     *  ⚠ THIS IS THE SECOND COPY OF A DEFECT ALREADY FIXED IN `kSPRest2.ts` ON 2026-08-01, and it
     *  survived because the two classes are reached by different hosts. `kSPRest` OVERRIDES
     *  `RestExecute` for the browser, so every browser caller has been getting the merged headers
     *  since that date; this core is what the SERVER-SIDE executors use, and it still assigned the
     *  caller's object directly. `_ApplyRequestHeaders` in that file carries the original
     *  reasoning - the browser lost `X-RequestDigest` and answered 403; here there is no digest,
     *  so what is lost is `Accept` and `Content-Type` and SharePoint answers
     *    415 "The HTTP header Content-Type is missing or its value is invalid."
     *
     *  RUNTIME-CONFIRMED 2026-09-18 against SharePoint Online, ContractsManager's CM service: the
     *  daily pass could not claim its run lease, and EVERY write the service makes uses the same
     *  natural shape for a tunnelled write -
     *    { "IF-MATCH": Etag, "X-HTTP-Method": "MERGE" }
     *  - so the contract-number counter, the audit counter, ticket spending, the role-grant lapse
     *  and the sensitivity revoke were all refused too. No gate could see it: every one of them
     *  compares source with source, and every test fakes the executor.
     *
     *  Merging is strictly safer: the caller's own keys still win (an explicit `X-HTTP-Method`,
     *  `IF-MATCH` or `Accept` overrides the base), and it can no longer lose a header the caller
     *  never mentioned. A STRING CustomHeader is not headers at all - it is a method name handed
     *  to BuildHeaders, which is why that branch is untouched.
     *
     *  ⚠ THE `KErrorMode` GUARD BELOW IS THE ONE LINE IN HERE THAT COULD RE-CREATE THE 415, AND IT
     *  IS UNREACHABLE TODAY - VERIFIED, NOT ASSUMED (2026-09-18). Returning early leaves
     *  `Options.Headers` at its `RestRequestOptions` default of `{}`, so the request would go out
     *  with no `Accept` and no `Content-Type`: the same headerless request this method was fixed to
     *  stop, reached through the gate instead of through a replace. It cannot fire, because every
     *  path already gates before it - `RestExecute` on its first line, `_BuildRequestOptions` (the
     *  caller at the second site) on its own first line, and `RestExecuteExternal` likewise - and
     *  nothing between those checks and this call reports.
     *  **So the hazard is only this: a THIRD call site added without its own gate check, or a
     *  caller's guard removed, fails as a headerless request rather than as a refusal.** Deliberately
     *  NOT "fixed" by setting default headers in the guarded branch: under a latched gate the request
     *  must not go out at all, and composing headers there would imply it may. If you add a call
     *  site, gate it. Found by contractsmanager-8c reviewing the fix above. */
    // DOCUMENTED EXCEPTION: moved body of kSPRestCore._ApplyHeaders - its gate, try/catch and handler label "kSPRestCore._ApplyHeaders" stay verbatim, so error reports are unchanged.
    public static _ApplyHeaders = (Self: typeof kSPRestCore, Options: RestRequestOptions, CustomHeader: string | Record<string, string> | null | undefined): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (kNotNullOrUndefined(CustomHeader)) {
                if (typeof CustomHeader === "object" && CustomHeader !== null) {
                    const Merged: Record<string, string> =
                        Self.BuildHeaders(Options.Method);
                    for (const Key of Object.keys(CustomHeader)) {
                        Merged[Key] = CustomHeader[Key];
                    }
                    Options.Headers = Merged;
                } else {
                    Options.Headers = Self.BuildHeaders(nz(CustomHeader));
                }
            } else {
                Options.Headers = Self.BuildHeaders(Options.Method);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore._ApplyHeaders");
        }
    };

    /**
     * RestExecuteWebService's ExtraHeaders, added 2026-09-14, OPTIONAL so every existing caller is
     * byte-unchanged. A service that authenticates its caller needs the credential somewhere, and the
     * only alternatives were a query string (which lands in every access log and proxy cache) or a raw
     * promise-based fetch, which is banned and would also opt out of the retry policy and the single
     * error channel. Merged AFTER the base so a caller can deliberately override Content-Type, and each
     * value is stringified rather than trusted - a header whose value is a number or null becomes "null"
     * in some transports and silently drops in others. (Moved out of RestExecuteWebService 2026-10-10.)
     */
    // DOCUMENTED EXCEPTION: moved body of kSPRestCore._MergeExtraHeaders - its gate, try/catch and handler label "kSPRestCore._MergeExtraHeaders" stay verbatim, so error reports are unchanged.
    public static _MergeExtraHeaders = (Self: typeof kSPRestCore, Options: RestRequestOptions, ExtraHeaders: Record<string, string> | null | undefined): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (ExtraHeaders === null || ExtraHeaders === undefined) { return; }
            for (const Name of Object.keys(ExtraHeaders)) {
                Options.Headers[Name] = String(ExtraHeaders[Name]);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore._MergeExtraHeaders");
        }
    };

    // DOCUMENTED EXCEPTION: moved body of kSPRestCore._ApplyPayload - its gate, try/catch and handler label "kSPRestCore._ApplyPayload" stay verbatim, so error reports are unchanged.
    public static _ApplyPayload = (Self: typeof kSPRestCore, Options: RestRequestOptions, Data: string | Record<string, any> | Blob | ArrayBuffer | null | undefined): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (kNotNullOrUndefined(Data)) {
                if ((typeof Blob !== "undefined" && Data instanceof Blob) ||
                    (typeof ArrayBuffer !== "undefined" && Data instanceof ArrayBuffer)) {
                    Options.BinaryData = Data;
                } else if (typeof Data !== "string") {
                    // Sanitise every string VALUE while serialising so a text field can never carry a
                    // character SharePoint rejects (see SanitizeTextForWrite). An already-serialised raw
                    // string body (the else branch — e.g. a $batch multipart payload) is left untouched.
                    Options.Data = JSON.stringify(Data, Self["_SanitizeReplacer"]);
                } else {
                    Options.Data = Data;
                }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore._ApplyPayload");
        }
    };

    /** JSON.stringify replacer for a SharePoint write body: sanitises every string VALUE (the
        replacer is invoked for each nested property, so nested strings are covered too). Field
        NAMES (keys) are code-controlled internal names and pass through untouched; non-strings
        pass through. DOCUMENTED EXCEPTION: pure deterministic replacer — no ks/DOM/IO, cannot
        meaningfully throw; its sole caller (_ApplyPayload) is fully guarded and try/caught. */
    public static _SanitizeReplacer = (Self: typeof kSPRestCore, _Key: string, Value: unknown): unknown => {
        if (typeof Value !== "string") { return Value; }
        return Self.SanitizeTextForWrite(Value);
    };

    /** Remove the characters a SharePoint text field cannot store — the XML-1.0-illegal set: the C0
        control block below U+0020 EXCEPT tab (U+0009), LF (U+000A), CR (U+000D), plus U+FFFE / U+FFFF.
        Writing such a byte fails the WHOLE item with SPException -2130575336 ("Invalid text value"),
        so any project pushing user/customer-derived text (names, notes, rendered HTML) through
        RestExecute is protected here — RUNTIME-CONFIRMED 2026-07-12 when a control character in a
        synced value halted a live SharePoint write. Char-code based (never a literal-control-char
        regex, which would embed the raw bytes in source); a clean string is returned unchanged (no
        allocation on the common path). DOCUMENTED EXCEPTION: pure deterministic helper — no ks/DOM/IO,
        cannot throw; called during serialisation from the guarded _ApplyPayload. */
    public static SanitizeTextForWrite = (Self: typeof kSPRestCore, Value: string): string => {
        let HasIllegal: boolean = false;
        for (let I: number = 0; I < Value.length; I = I + 1) {
            if (Self["_IsStorableChar"](Value.charCodeAt(I)) === false) { HasIllegal = true; break; }
        }
        if (HasIllegal === false) { return Value; }
        let Clean: string = "";
        for (let J: number = 0; J < Value.length; J = J + 1) {
            if (Self["_IsStorableChar"](Value.charCodeAt(J)) === true) { Clean = Clean + Value.charAt(J); }
        }
        return Clean;
    };

    /** True when a code point is storable in a SharePoint text field (XML-1.0-legal).
        DOCUMENTED EXCEPTION: pure deterministic predicate — no ks/DOM/IO, cannot throw. */
    public static _IsStorableChar = (Self: typeof kSPRestCore, Code: number): boolean => {
        if (Code === 0x09 || Code === 0x0A || Code === 0x0D) { return true; }
        if (Code < 0x20) { return false; }
        if (Code === 0xFFFE || Code === 0xFFFF) { return false; }
        return true;
    };

    // DOCUMENTED EXCEPTION: moved body of kSPRestCore._BuildRequestContext - its gate, try/catch and handler label "kSPRestCore._BuildRequestContext" stay verbatim, so error reports are unchanged.
    public static _BuildRequestContext = (Self: typeof kSPRestCore, Options: RestRequestOptions, Callback: any, Comments: string | undefined, IgnoreError: boolean | undefined, ErrorHandler: any, TolerateStatuses?: number[]): RestRequestContext => {
        if (getCoreUtils().KErrorMode) { return new RestRequestContext(); }
        try {
            const Context: RestRequestContext = new RestRequestContext();
            Context.Options = Options;
            if (kNotNullOrUndefined(Callback)) {
                Context.Callback = Callback;
            }
            Context.Comments = nz(Comments);
            Context.IgnoreError = nb(IgnoreError);
            if (kNotNullOrUndefined(TolerateStatuses)) {
                Context.TolerateStatuses = TolerateStatuses as number[];
            }
            if (kNotNullOrUndefined(ErrorHandler)) {
                Context.ErrorHandler = ErrorHandler;
            }
            return Context;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kSPRestCore._BuildRequestContext");
            return new RestRequestContext();
        }
    };
}
